// Always-visible "reset this browser" affordance — DEV BUILDS ONLY.
//
// Sits in the bottom-left corner over every route so a developer
// carrying stale identity + study payloads from a half-shipped
// feature branch can wipe local state and start fresh without
// opening DevTools. Production builds render nothing.
//
// Scope: shares `clearAllStorage()` with the boot-time schema-
// version guard so the manual + automatic paths wipe the same
// surface — localStorage, sessionStorage, plus the SharedWorker
// broker's IndexedDB snapshot (`helm-broker`) used in IPC mode.
//
// Intentionally does NOT touch cookies or service-worker caches —
// same blast radius as DevTools → "Clear site data" minus the
// bits that aren't really "local" to this browser.

import { Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { clearAllStorage } from './storage_version';

const PROMPT_MS = 4000;

export interface ClearLocalStorageButtonProps {
  /** Force the gate one way or the other. Tests pass an explicit
   *  value; callers in production leave it unset and the shim
   *  reads `import.meta.env.DEV`. */
  isDev?: boolean;
}

/**
 * Render the dev-only clear-local-data pill. In production builds
 * (`import.meta.env.DEV === false`) this is a no-op so a real
 * researcher in a lab can never trip on it.
 */
export function ClearLocalStorageButton({ isDev }: ClearLocalStorageButtonProps = {}) {
  const dev = isDev ?? isDevBuild();
  if (!dev) return null;
  return <ClearLocalStorageButtonInner />;
}

function isDevBuild(): boolean {
  // Vite replaces `import.meta.env.DEV` at build time; node-side
  // tests without Vite leave the property undefined. Wrapped in a
  // try so a stricter `import.meta` shape never breaks module init.
  try {
    const v = (import.meta as unknown as { env?: { DEV?: unknown } }).env;
    return v?.DEV === true;
  } catch {
    return false;
  }
}

function ClearLocalStorageButtonInner() {
  // Two-tap UX: first click switches the button into "armed" mode for
  // PROMPT_MS; the second click within the window actually clears.
  // A native `window.confirm` would do the same job with less code,
  // but it's modal + ugly + blocks the test-driver. The inline
  // state keeps the affordance unobtrusive.
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const onClick = useCallback(() => {
    if (busy) return;
    if (!armed) {
      setArmed(true);
      window.setTimeout(() => setArmed(false), PROMPT_MS);
      return;
    }
    setBusy(true);
    // clearAllStorage is async because IDB deletes are. We can't
    // call reload until the deletes have at least been issued —
    // otherwise the page reloads, the IDB connection drops, and
    // the new boot reads the not-yet-cleared snapshot.
    void clearAllStorage().then(() => {
      window.location.hash = '#/';
      window.location.reload();
    });
  }, [armed, busy]);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={
        armed
          ? 'Confirm clear all local storage'
          : 'Clear all local storage on this browser (dev only)'
      }
      title={
        armed
          ? 'Click again to wipe localStorage + sessionStorage + broker IDB and reload.'
          : 'Dev affordance: wipe local storage on this browser (identity, offline studies, broker IDB, …).'
      }
      data-testid="clear-localstorage"
      data-armed={armed ? 'true' : 'false'}
      className={
        'fixed bottom-3 left-3 z-50 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium shadow-sm backdrop-blur transition ' +
        (armed
          ? 'border-destructive bg-destructive text-destructive-foreground'
          : 'border-border bg-card/90 text-muted-foreground hover:border-destructive/40 hover:text-destructive')
      }
    >
      <Trash2 className="size-3" />
      {busy ? 'Clearing…' : armed ? 'Click again to wipe' : 'Clear local data (dev)'}
    </button>
  );
}
